import React from 'react';
import { X } from 'lucide-react';
import {
  audioInputs,
  chainForVideo,
  upstreamVideos,
  videoSeconds,
  type Board,
  type BoardAudioNode,
  type BoardImageNode,
  type BoardNode,
  type BoardVideoNode,
} from '../../lib/canvasBoard';
import { CAMERA_MOVE_PT, ENERGY_PT, PACE_PT, TRANSITION_PT } from '../../lib/fragmentadorPromptContract';
import { getVideoModelById } from '../../lib/videoModelCatalog';
import { getAudioModelById } from '../../lib/audioModelCatalog';

type Props = {
  board: Board;
  node: BoardNode;
  sceneIndex: Map<string, number>;
  style: React.CSSProperties;
  onClose: () => void;
};

const secs = (n: number) => `${(Math.round(n * 10) / 10).toLocaleString('pt-BR')}s`;

const AUDIO_KIND = { voice: 'Locução', music: 'Música de fundo', sfx: 'Efeito sonoro' } as const;
const INTENT_PT: Record<string, string> = {
  camera_showcase: 'Passeio de câmera',
  progressive_build: 'Construção progressiva',
  subject_evolution: 'Evolução do assunto',
  cinematic_montage: 'Montagem cinematográfica',
};

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  if (children == null || children === '' || children === false) return null;
  return (
    <div className="tb-info-field">
      <span>{label}</span>
      <div>{children}</div>
    </div>
  );
}

function ImageInfo({ node, scene }: { node: BoardImageNode; scene?: number }) {
  const p = node.phase;
  return (
    <>
      <h3>{scene != null ? `Cena ${scene} · ` : ''}{p.label || 'Imagem'}</h3>
      {p.t_end > 0 && <div className="tb-info-time">{secs(p.t_start)} – {secs(p.t_end)}</div>}
      <Field label="O que acontece">{p.description_pt}</Field>
      <Field label="Câmera">{CAMERA_MOVE_PT[p.camera_move]}</Field>
      <Field label="Ritmo · energia">{p.pace && p.energy ? `${PACE_PT[p.pace]} · ${ENERGY_PT[p.energy]}` : null}</Field>
      <Field label="Para a próxima cena">{p.transition_out ? TRANSITION_PT[p.transition_out] : null}</Field>
      {p.video_prompt_en && (
        <details className="tb-info-prompt">
          <summary>Prompt do vídeo (inglês)</summary>
          <p>{p.video_prompt_en}</p>
        </details>
      )}
    </>
  );
}

function VideoInfo({ board, node, sceneIndex }: { board: Board; node: BoardVideoNode; sceneIndex: Map<string, number> }) {
  const chain = chainForVideo(board, node.id);
  const ups = upstreamVideos(board, node.id);
  const seconds = videoSeconds(board, node.id);
  const model = node.videoModelId ? getVideoModelById(node.videoModelId)?.label : undefined;
  const role = node.edit ? 'Vídeo editado' : chain.length ? 'Fluxo de cenas' : ups.length ? 'Junção de vídeos' : node.fromGallery ? 'Vídeo da galeria' : 'Vídeo';
  return (
    <>
      <h3>{node.title || 'Vídeo'}</h3>
      <div className="tb-info-time">{role}{seconds ? ` · ${secs(seconds)}` : ''}</div>
      <Field label="Roteiro">{node.brief}</Field>
      <Field label="Estilo">
        {[node.look === 'cinematic' ? 'Cinematográfico' : node.look === 'realistic' ? 'Realista' : null, node.intentHint ? INTENT_PT[node.intentHint] : null]
          .filter(Boolean).join(' · ') || null}
      </Field>
      <Field label="Modelo">{model ? `${model}${node.resolutionId ? ` · ${node.resolutionId}` : ''}` : null}</Field>
      {chain.length > 0 && (
        <div className="tb-info-field">
          <span>Cenas</span>
          <ol className="tb-info-list">
            {chain.map((n) => (
              <li key={n.id}>
                <strong>{sceneIndex.get(n.id) ?? ''} {n.phase.label}</strong>
                {n.phase.t_end > 0 && <em> {secs(n.phase.t_start)}–{secs(n.phase.t_end)}</em>}
                {n.phase.description_pt && <p>{n.phase.description_pt}</p>}
              </li>
            ))}
          </ol>
        </div>
      )}
      {!node.edit && ups.length > 0 && (
        <div className="tb-info-field">
          <span>Ordem da junção</span>
          <ol className="tb-info-list">
            {ups.map((u) => <li key={u.id}><strong>{u.title || 'Vídeo'}</strong></li>)}
          </ol>
          {node.joinTransition && (
            <p className="tb-info-note">
              {node.joinTransition.kind === 'crossfade' ? 'Transição suave' : 'Fade pelo preto'} de {secs(node.joinTransition.seconds)} entre os vídeos
            </p>
          )}
        </div>
      )}
      {node.edit && (
        <>
          <Field label="Vídeo de origem">{ups[0]?.title || null}</Field>
          <Field label="Faixas de som">
            {audioInputs(board, node.id).map((a) => `${a.title || AUDIO_KIND[a.audioKind]} (${Math.round(a.volume * 100)}%)`).join(', ') || null}
          </Field>
          <Field label="Ajustes">
            {[
              `som original ${Math.round(node.edit.sourceVolume * 100)}%`,
              node.edit.captions ? 'legendas' : null,
              node.edit.fade ? 'fade no começo e no fim' : null,
              node.edit.grayscale ? 'preto e branco' : node.edit.grade ? 'cor cinematográfica' : null,
            ].filter(Boolean).join(' · ')}
          </Field>
        </>
      )}
    </>
  );
}

function AudioInfo({ node }: { node: BoardAudioNode }) {
  const model = node.voiceModelId ? getAudioModelById(node.voiceModelId)?.label : undefined;
  return (
    <>
      <h3>{node.title || AUDIO_KIND[node.audioKind]}</h3>
      <div className="tb-info-time">{AUDIO_KIND[node.audioKind]}{node.seconds ? ` · ${secs(node.seconds)}` : ''}</div>
      <Field label={node.audioKind === 'voice' ? 'Texto da locução' : 'Descrição do som'}>{node.text}</Field>
      <Field label="Modelo">{model ? `${model}${node.voiceId ? ` · ${node.voiceId}` : ''}` : null}</Field>
      <Field label="Mixagem">
        {[
          `volume ${Math.round(node.volume * 100)}%`,
          node.startOffset ? `começa em ${secs(node.startOffset)}` : null,
          node.loop ? 'em loop' : null,
          node.speed && node.speed !== 1 ? `${node.speed}×` : null,
          node.muted ? 'silenciado' : null,
        ].filter(Boolean).join(' · ')}
      </Field>
    </>
  );
}

/** Script details of one node (scene text, camera, flow, sound text) for anyone reading the board. */
export default function CanvasNodeInfo({ board, node, sceneIndex, style, onClose }: Props) {
  return (
    <div className="tb-info" style={style} onPointerDown={(e) => e.stopPropagation()} role="dialog" aria-label="Roteiro do nó">
      <button type="button" className="tb-info-close" aria-label="Fechar" onClick={onClose}>
        <X size={13} />
      </button>
      {node.kind === 'image' ? (
        <ImageInfo node={node} scene={sceneIndex.get(node.id)} />
      ) : node.kind === 'audio' ? (
        <AudioInfo node={node} />
      ) : node.kind === 'comment' ? (
        <>
          <h3>Comentário</h3>
          <Field label="Texto">{node.text}</Field>
        </>
      ) : (
        <VideoInfo board={board} node={node} sceneIndex={sceneIndex} />
      )}
    </div>
  );
}
